import { Helmet } from 'react-helmet';
import { Button, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import Cookies from 'js-cookie';

import { useLocalization } from 'localization';
import { loginSchema } from '../../../components/v2/division/form/schema/loginSchema';
import { InputField } from 'components/v2/input/InputField';
import ClickableImage from 'components/v2/image/ClickableImage';
import { authApiMutation } from 'api/auth/query';
import appRoutes from 'app.routes';
import { EFKT_SITE_URL } from 'api/http';
import routes from 'app.routes';
import { isPilotUser } from 'helpers/isPilotUser';
import { redirectToOldSite } from 'helpers/redirectToOldSite';

type FormData = {
  email: string;
};

const Login = () => {
  const { t } = useLocalization();
  const schema = loginSchema();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  const { control, handleSubmit } = methods;
  const { mutateAsync, isPending: isLoading } = authApiMutation();
  const isUserLoggedIn = Cookies.get('auth.token');

  const onSubmit = async ({ email }: FormData) => {
    const normalizedEmail = email.toLowerCase().trim();
    const isInternalUser = normalizedEmail.endsWith('@esoft.com') || normalizedEmail.endsWith('@efkt.com');
    const isPilot = await isPilotUser(email);
    // TODO: [EP-1100] This is a temporary fix which is for preventing non-whitelisted users to access the system. This will be removed once the application is ready and everyone can access. (https://esofttech.atlassian.net/browse/EP-1100)
    if (!isPilot && !isInternalUser) {
      return redirectToOldSite();
    }
    await mutateAsync({ email, appRoute: appRoutes.authRedirect() });
  };

  if (isUserLoggedIn) {
    //prevent users from accessing the login page
    //if they have logged in
    window.location.href = routes.v2.dashboard;
    return null;
  }

  return (
    <>
      <Helmet>
        <meta property="og:url" content="https://booqly.dk" />
        <meta property="og:type" content="Website" />
        <meta property="og:title" content={t('auth.og.title')} />
        <meta property="og:description" content={t('auth.og.subTitle')} />
        <meta property="og:image" content="https://dev.booqly.dk/logo.png" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="da_DK" />
        <meta property="og:locale:alternate" content="sv_SE" />
      </Helmet>
      <Stack height="100%" sx={{ padding: '1.5rem' }}>
        <Stack width="100%">
          <Button
            type="submit"
            sx={{ alignSelf: 'flex-start', height: '2rem' }}
            variant="outlined"
            startIcon={<KeyboardBackspaceIcon />}
            onClick={() => open(EFKT_SITE_URL, '_self')}
          >
            {t('common.terms.backButton')}
          </Button>
        </Stack>
        <Stack
          sx={{
            width: {
              xs: '100%',
              lg: '66.67%',
              xl: '50%',
            },
            mx: 'auto',
            my: 'auto',
          }}
          justifyContent="space-around"
          alignSelf="center"
          alignItems="center"
          height="100%"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              gap={3}
              sx={{
                width: { xs: '100%', lg: '50%' },
                minWidth: { xs: 'unset', lg: '25rem' },
              }}
            >
              <Stack mb={2} width="100%">
                <ClickableImage path="" imgSrc="/logo.png" width="4.44rem" height="1.5rem" />
              </Stack>
              <Stack mb={2} gap={2} width="100%">
                <Typography variant="h3" lineHeight={1.25}>
                  {t('auth.pages.login.greeting')}
                </Typography>
              </Stack>
              <Stack width="100%">
                <InputField
                  control={control}
                  placeholder="common.terms.email"
                  required
                  label="common.terms.email"
                  name="email"
                />
              </Stack>
              <Stack width="100%">
                <Button
                  type="submit"
                  loading={isLoading}
                  sx={{
                    alignSelf: 'flex-end',
                  }}
                  variant="contained"
                  endIcon={<ArrowRightAltIcon />}
                >
                  {t('common.terms.continue')}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </Stack>
    </>
  );
};

export default Login;
